import type { WorkerLogtimeMonthlyV2Response } from "@lhd/hooks";
import type { CSSProperties } from "react";
import { getTrackingStatusLabel } from "../../until";
import { formatIsoInTokyo } from "../../workLogTimezone";

export type PdfColumn = {
  header: string;
  dataKey: string;
  style?: CSSProperties;
};
export type PdfRow = Record<string, string>;
export type MonthlyPdfTable = { columns: PdfColumn[]; rows: PdfRow[] };

export const parseMonthlyPdfData = (
  data?: WorkerLogtimeMonthlyV2Response,
): MonthlyPdfTable => {
  if (!data) return { columns: [], rows: [] };

  const records = data.data ?? [];
  const maxBreakPairs = records.reduce(
    (maximum, record) =>
      Math.max(maximum, record.corrected?.breaks?.length ?? 0),
    0,
  );
  const columns: PdfColumn[] = [
    {
      header: "スタッフ名",
      dataKey: "workerName",
      style: {
        minWidth: 110,
        maxWidth: 170,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      },
    },
    { header: "日付", dataKey: "date" },
    { header: "出勤", dataKey: "correctedCheckIn" },
    { header: "退勤", dataKey: "correctedCheckOut" },
  ];

  for (let index = 0; index < maxBreakPairs; index += 1) {
    columns.push(
      {
        header: "休憩開始",
        dataKey: `correctedBreakStart${index}`,
      },
      {
        header: "休憩終了",
        dataKey: `correctedBreakEnd${index}`,
      },
    );
  }

  columns.push(
    {
      header: "MEMO",
      dataKey: "memo",
      style: {
        minWidth: 100,
        textAlign: "left",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      },
    },
    { header: "労働時間", dataKey: "workingTime" },
    { header: "休憩時間", dataKey: "breakTime" },
    { header: "ステータス", dataKey: "status" },
    {
      header: "更新者",
      dataKey: "updaterName",
      style: {
        maxWidth: 150,
        minWidth: 100,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      },
    },
    { header: "更新日時", dataKey: "updatedAt" },
  );

  const rows: PdfRow[] = records.map((record) => {
    const row: PdfRow = {
      workerName: record.worker_name ?? "",
      date: record.date ?? "",
      correctedCheckIn: record.corrected?.check_in?.display_time ?? "",
      correctedCheckOut: record.check_out_missing
        ? "未入力"
        : record.corrected?.check_out?.display_time ?? "",
      memo: record.note ?? "",
      workingTime: record.working_time ?? "",
      breakTime: record.break_time ?? "",
      status: getTrackingStatusLabel(record.logtime_status?.trim() ?? ""),
      updaterName: record.updater_name ?? "",
      updatedAt: formatIsoInTokyo(record.updated_at, "YYYY/MM/DD HH:mm"),
    };
    for (let index = 0; index < maxBreakPairs; index += 1) {
      const breakPair = record.corrected?.breaks?.[index];
      const isBreakEndMissing =
        Boolean(breakPair?.start?.display_time) &&
        !breakPair?.end?.display_time;

      row[`correctedBreakStart${index}`] = breakPair?.start?.display_time ?? "";
      row[`correctedBreakEnd${index}`] = isBreakEndMissing
        ? "未入力"
        : breakPair?.end?.display_time ?? "";
    }
    return row;
  });

  return { columns, rows };
};
